/* global __firebase_config, __app_id */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Printer, BarChart2, Users, Plus, Search, Trash2, CheckCircle, Clock, Package, X, AlertTriangle, MessageSquare, Edit, Download, Upload, Settings, LogIn, UserPlus, Tag, LogOut, ChevronLeft, ChevronRight, Flame, Wifi, WifiOff, List, Trophy, Lightbulb, Save, Phone, AlertCircle, Moon } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { initializeApp } from "firebase/app";
import { getFirestore, collection, onSnapshot, doc, addDoc, setDoc, deleteDoc, updateDoc, writeBatch, getDocs, getDoc, query, where, enableIndexedDbPersistence, orderBy, limit, startAfter } from "firebase/firestore";
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";

// --- CONFIGURAZIONE ---
const VERSION = "1.8.8"; // Final code cleanup and review
const ORDERS_PER_PAGE = 30;
// INSERISCI QUI IL TUO ID DI MISURAZIONE DI GOOGLE ANALYTICS
const GA_MEASUREMENT_ID = "G-23X28WWQXJ"; // Sostituisci G-XXXXXXXXXX con il tuo vero ID

const firebaseConfig = {
  apiKey: "AIzaSyBtzOKHvDCrSv1we4F1nTPzokfcVde3F5g",
  authDomain: "gestionalestrasso.firebaseapp.com",
  projectId: "gestionalestrasso",
  storageBucket: "gestionalestrasso.firebasestorage.app",
  messagingSenderId: "777700469219",
  appId: "1:777700469219:web:2e7b8b71d6e2f764ea98d6"
};
const appId = firebaseConfig.projectId || 'default-laundry-app';

let app;
let db;
let auth;

try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    enableIndexedDbPersistence(db)
      .catch((err) => {
          if (err.code === 'failed-precondition') {
              console.warn("Persistenza offline fallita: più schede aperte. Funzionerà solo su una scheda.");
          } else if (err.code === 'unimplemented') {
              console.warn("Questo browser non supporta la persistenza offline.");
          }
      });
    auth = getAuth(app);
} catch (error) {
    console.error("Errore di configurazione Firebase. Controlla le chiavi nel codice.", error);
}


// --- STILI E DATI GLOBALI ---
const COLORS = {
  primary: '#c0a062',
  secondary: '#f1e9d4',
  text: '#333333',
  background: '#fdfcf8',
  lightGray: '#e5e7eb',
  success: '#22c55e',
  warning: '#f59e0b',
  info: '#3b82f6',
};

const STATUS_CONFIG = {
  'In Lavorazione': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="w-4 h-4" />, color: COLORS.warning },
  'Pronto': { bg: 'bg-blue-100', text: 'text-blue-800', icon: <CheckCircle className="w-4 h-4" />, color: COLORS.info },
  'Ritirato': { bg: 'bg-green-100', text: 'text-green-800', icon: <Package className="w-4 h-4" />, color: COLORS.success },
};


// --- COMPONENTI UI ---
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white/80 backdrop-blur-lg border border-white/30 rounded-lg shadow-2xl w-full max-w-lg max-h-full overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b"><h3 className="text-xl font-semibold font-display" style={{ color: COLORS.text }}>{title}</h3><button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button></div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
        <div className="text-center">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
            <p className="mb-6 text-lg" style={{color: COLORS.text}}>{message}</p>
            <div className="flex justify-center gap-4"><button onClick={onClose} className="px-6 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold">Annulla</button><button onClick={onConfirm} className="px-6 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold">Conferma</button></div>
        </div>
    </Modal>
);

const PasswordConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, error }) => {
    const [password, setPassword] = useState('');

    const handleConfirm = () => {
        onConfirm(password);
        setPassword('');
    };

    const handleClose = () => {
        setPassword('');
        onClose();
    };

    if (!isOpen) return null;
    
    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={title}>
            <div className="text-center">
                <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
                <p className="mb-4 text-lg">{message}</p>
                <p className="mb-4 text-sm text-gray-600">Per procedere, inserisci la tua password.</p>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full max-w-xs mx-auto px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                />
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                <div className="flex justify-center gap-4 mt-6">
                    <button onClick={handleClose} className="px-6 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold">Annulla</button>
                    <button onClick={handleConfirm} className="px-6 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold">Conferma</button>
                </div>
            </div>
        </Modal>
    );
};

const HangerTicket = ({ order }) => {
    if (!order) return null;
    return (
        <div id="hanger-ticket-content">
            <h2>Etichetta di Lavorazione</h2>
            {order.priority && <div className="urgent-indicator">*** URGENTE ***</div>}
            <hr />
            <p><strong>Cliente:</strong> {order.clientName}</p>
            <p><strong>Ordine No:</strong> {order.orderNumber}</p>
            <hr />
            <table>
                <tbody>
                    {order.garments.map((g, i) => (
                        <tr key={i}>
                            <td>
                                {g.name}
                                {g.serviceDescription && ` (${g.serviceDescription})`}
                                {g.notes && <span className="item-note"><strong>Nota:</strong> {g.notes}</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const CustomerReceipt = ({ order }) => {
    const groupedGarments = useMemo(() => {
        if (!order || !order.garments) return [];
        
        const garmentGroups = order.garments.reduce((acc, garment) => {
            const key = `${garment.name}-${garment.price}-${garment.serviceDescription}`;
            if (!acc[key]) {
                acc[key] = {
                    name: garment.name,
                    quantity: 0,
                    price: parseFloat(garment.price || 0),
                    notes: [],
                    serviceDescription: garment.serviceDescription
                };
            }
            acc[key].quantity++;
            if (garment.notes) {
                acc[key].notes.push(garment.notes);
            }
            return acc;
        }, {});
        
        return Object.values(garmentGroups);
    }, [order]);

    if (!order) return null;

    return (
        <div id="receipt-content">
            <div className="header">
                <img src="https://i.imgur.com/ANHwUR3.png" alt="Logo Lavanderia Strasso" className="logo"/>
                <div className="store-details">
                    <p className="store-name">Lavanderia Strasso</p>
                    <p>Corso Nazionale 398</p>
                    <p>Scafati (SA)</p>
                    <p>Tel: 333 479 1469</p>
                </div>
            </div>
            <hr className="hr-solid" />
            <div className="order-details">
                <div>
                    <p><strong>Cliente:</strong> {order.clientName}</p>
                    <p><strong>Data:</strong> {new Date(order.createdAt).toLocaleDateString('it-IT')}</p>
                </div>
                <div className="order-number-section">
                    <p className="order-label">ORDINE N.</p>
                    <p className="order-number">{order.orderNumber}</p>
                </div>
            </div>
            <hr className="hr-dashed" />
            <table className="items-table">
                <tbody>
                    {groupedGarments.map((g, i) => (
                        <React.Fragment key={i}>
                            <tr className="item-row">
                                <td className="item-name">
                                    {g.quantity > 1 && `${g.quantity}x `}{g.name}
                                    {g.serviceDescription && <span className="service-type-tag"> ({g.serviceDescription})</span>}
                                </td>
                                <td className="item-price">€{parseFloat(g.price || 0).toFixed(2)}</td>
                            </tr>
                            {g.notes.map((note, noteIndex) => (
                                note && <tr key={noteIndex} className="note-row">
                                    <td colSpan="2" className="item-note"><strong>Nota:</strong> {note}</td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            <hr className="hr-dashed" />
            <div className="total-section">
                <p className="total-label">TOTALE: €{order.total.toFixed(2)}</p>
            </div>
            <hr className="hr-solid" />
            <div className="footer">
                <p>Traccia lo stato del tuo ordine inserendo il suo numero su:</p>
                <p className="font-bold">ordinestrasso.it</p>
                <img src="https://i.imgur.com/fXmc7SR.png" alt="QR Code Tracciamento Ordine" className="qr-code"/>
            </div>
        </div>
    );
};

const Header = ({ navigateTo, isOnline }) => {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  useEffect(() => { const timerId = setInterval(() => setCurrentDateTime(new Date()), 1000); return () => clearInterval(timerId); }, []);
  const formattedDate = currentDateTime.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  const formattedTime = currentDateTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const ConnectionStatusIndicator = () => (
      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {isOnline ? <Wifi size={14}/> : <WifiOff size={14}/>}
          {isOnline ? 'Online' : 'Offline'}
      </div>
  );
  return (
    <header className="bg-white/70 backdrop-blur-lg border-b border-white/30 p-4 flex justify-between items-center shadow-sm sticky top-0 z-20">
      <div className="w-1/3"><h1 className="text-2xl md:text-3xl font-display" style={{ color: COLORS.text}}>Lavanderia Strasso</h1></div>
      <div className="w-1/3 flex justify-center"><button onClick={() => navigateTo('dashboard')} className="cursor-pointer"><img src="https://i.imgur.com/ANHwUR3.png" alt="Logo Lavanderia Strasso" className="h-16 object-contain"/></button></div>
      <div className="w-1/3 flex justify-end items-center gap-4">
        <ConnectionStatusIndicator />
        <div className="text-right">
            <div className="font-semibold text-sm md:text-lg capitalize" style={{ color: COLORS.text }}>{formattedDate}</div>
            <div className="text-xl md:text-2xl" style={{ color: COLORS.primary }}>{formattedTime}</div>
        </div>
      </div>
    </header>
  );
};

const DashboardView = ({ stats, navigateTo }) => {
    const { activeOrders, inProgressOrders, clients, topClients, urgentOrders, focusItems, dailySummary, ordersToPickup } = stats;

    const StatCard = ({ icon, title, value, color, onClick = null }) => {
        const content = (<div className="bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/20 flex items-center space-x-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"><div className="p-3 rounded-full" style={{backgroundColor: `${color}20`}}>{React.cloneElement(icon, {style: {color: color}})}</div><div><p className="text-sm text-gray-500">{title}</p><p className="text-xl md:text-2xl font-bold" style={{color: COLORS.text}}>{value}</p></div></div>);
        return onClick ? <button onClick={onClick} className="w-full text-left">{content}</button> : content;
    };
    
    return (
        <div className="space-y-6">
                 {focusItems && focusItems.length > 0 && (
                     <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-xl shadow-lg">
                         <h3 className="text-lg font-bold text-blue-800 flex items-center mb-2"><Lightbulb className="mr-2"/>Focus del Giorno</h3>
                         <div className="max-h-36 overflow-y-auto pr-2">
                             <ul className="space-y-2">
                                 {focusItems.map((item, index) => (
                                     <li key={index} className="flex items-center text-sm text-blue-700">
                                         <span className="mr-2">{item.icon}</span>
                                         <span>{item.text}</span>
                                     </li>
                                 ))}
                             </ul>
                         </div>
                     </div>
                 )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard icon={<Package size={24} />} title="Ordini Attivi" value={activeOrders} color={COLORS.primary} onClick={() => navigateTo('orders', 'active')} />
                <StatCard icon={<Users size={24} />} title="Clienti Totali" value={clients} color={COLORS.info} onClick={() => navigateTo('clients')} />
                <StatCard icon={<Clock size={24} />} title="In Lavorazione" value={inProgressOrders} color={COLORS.warning} onClick={() => navigateTo('orders', 'In Lavorazione')} />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-4">
              {urgentOrders && urgentOrders.length > 0 && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl shadow-lg">
                      <h3 className="text-lg font-bold text-red-800 flex items-center"><Flame className="mr-2" />Ordini Urgenti</h3>
                      <div className="max-h-48 overflow-y-auto mt-2 pr-2">
                          <ul className="space-y-2">
                              {urgentOrders.map(order => {
                                  const groupedGarments = order.garments.reduce((acc, g) => {
                                      acc[g.name] = (acc[g.name] || 0) + 1;
                                      return acc;
                                  }, {});
                                  const garmentSummary = Object.entries(groupedGarments)
                                      .map(([name, qty]) => (qty > 1 ? `${qty}x ${name}` : name))
                                      .join(', ');

                                  return (
                                      <li key={order.id} className="text-sm text-red-700 border-b border-red-200/50 pb-2 last:border-b-0">
                                          <div className="font-bold">Ordine #{order.orderNumber} - {order.clientName}</div>
                                          <div className="pl-4 text-xs text-gray-700">{garmentSummary}</div>
                                      </li>
                                  );
                              })}
                          </ul>
                      </div>
                  </div>
              )}
               <div className={`bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-lg border border-white/20 h-full flex flex-col ${(!urgentOrders || urgentOrders.length === 0) ? 'lg:col-span-2' : ''}`}>
                  <h3 className="text-lg font-semibold flex items-center mb-2" style={{color: COLORS.text}}>
                    <Trophy className="mr-2 text-yellow-500" size={20} /> Top 5 Clienti (Mese)
                  </h3>
                   <ul className="space-y-2 text-sm">
                    {topClients && topClients.length > 0 ? topClients.map((client, index) => (
                      <li key={index} className="flex justify-between items-center">
                        <div className="flex items-center">
                            <span className="font-bold text-gray-400 mr-2">{index + 1}.</span>
                            <span>{client.name}</span>
                        </div>
                      </li>
                    )) : <p className="text-gray-500 text-xs text-center mt-4">Nessun dato per questo mese.</p>}
                   </ul>
              </div>
            </div>
            {dailySummary && (
                <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-lg border border-white/20">
                    <h3 className="text-lg font-semibold flex items-center mb-4" style={{color: COLORS.text}}>
                        <List className="mr-2 text-blue-500" size={20} /> Riepilogo di Oggi
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                            <p className="text-2xl font-bold" style={{color: COLORS.primary}}>{dailySummary.newOrders}</p>
                            <p className="text-sm text-gray-500">Nuovi Ordini</p>
                        </div>
                        {/* Questo box è cliccabile e porta alla lista degli ordini pronti */}
                        <div className="rounded-lg hover:bg-gray-100 transition-colors p-2 cursor-pointer" onClick={() => navigateTo('orders', 'Pronto')}>
                            <p className="text-2xl font-bold" style={{color: COLORS.info}}>{ordersToPickup}</p>
                            <p className="text-sm text-gray-500">Ordini da Ritirare</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold" style={{color: COLORS.info}}>{dailySummary.newClients}</p>
                            <p className="text-sm text-gray-500">Nuovi Clienti</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold" style={{color: COLORS.primary}}>{dailySummary.garments}</p>
                            <p className="text-sm text-gray-500">Capi Lavorati Oggi</p>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

const GroupedGarmentsList = ({ garments }) => {
    const grouped = useMemo(() => {
        if (!garments) return [];
        const garmentGroups = garments.reduce((acc, garment) => {
            const key = `${garment.name}-${garment.serviceDescription}-${garment.notes || ''}`;
            if (!acc[key]) {
                acc[key] = { ...garment, quantity: 0 };
            }
            acc[key].quantity++;
            return acc;
        }, {});
        return Object.values(garmentGroups);
    }, [garments]);

    return (
        <ul className="text-sm list-disc list-inside">
            {grouped.map((g, i) => (
                <li key={i}>
                    {g.quantity > 1 && `${g.quantity}x `}
                    {g.name}
                    {g.serviceDescription && <span className="text-xs text-gray-500 italic ml-1">({g.serviceDescription})</span>}
                    {g.notes && <span className="block text-xs text-gray-500 pl-4">- {g.notes}</span>}
                </li>
            ))}
        </ul>
    );
};

const OrdersView = ({ orders, clients, onStatusChange, onDeleteRequest, onPrintCustomerReceipt, onPrintHangerTicket, onSearchChange, searchTerm, onOpenEditOrderModal, orderStatusFilter, onFilterChange, filterCounts, selectedDate, onDateChange, onLoadMore, hasMore, isLoading }) => {
    const StatusBadge = ({ status }) => { const config = STATUS_CONFIG[status] || {bg: 'bg-gray-100', text: 'text-gray-800', icon: <Clock className="w-4 h-4"/>}; return (<span className={`inline-flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full ${config.bg} ${config.text}`}>{config.icon}{status}</span>);};
    const sendWhatsAppMessage = (phone, clientName) => { const message = encodeURIComponent(`Gentile ${clientName}, il suo ordine presso Lavanderia Strasso è pronto per il ritiro!`); window.open(`https://wa.me/${phone}?text=${message}`, '_blank');};
    const clientPhoneMap = useMemo(() => clients.reduce((acc, client) => { if (client.phone) acc[client.id] = client.phone; return acc; }, {}), [clients]);
    
    const FilterButton = ({ label, filter, currentFilter, onFilterChange, icon, count }) => {
        const isActive = filter === currentFilter;
        return (
            <button
                onClick={() => onFilterChange(filter)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${isActive ? 'bg-yellow-500 text-white shadow-md' : 'bg-white hover:bg-yellow-100/50 text-gray-700'}`}
            >
                {icon}
                {label}
                {count !== undefined && (
                  <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-gray-200'}`}>
                    {count}
                  </span>
                )}
            </button>
        );
    };
    
    const monthNames = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

    // Applica la ricerca solo sugli ordini già caricati
    const displayedOrders = useMemo(() => {
        if (!searchTerm) return orders;
        return orders.filter(order =>
            order.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (order.orderNumber && order.orderNumber.toString().includes(searchTerm))
        );
    }, [orders, searchTerm]);

    return (
        <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-lg border border-white/20">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 flex-wrap gap-4">
                <h2 className="text-2xl font-bold font-display" style={{color: COLORS.text}}>Elenco Ordini</h2>
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20}/><input type="text" placeholder="Cerca cliente o ID ordine..." value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 w-full" /></div>
            </div>
            
            <div className="bg-gray-100/50 p-2 rounded-lg mb-4">
                <div className="flex items-center justify-center mb-1">
                    <button onClick={() => onDateChange({year: selectedDate.year - 1, month: selectedDate.month})} className="p-1 hover:bg-gray-200 rounded-full"><ChevronLeft size={18}/></button>
                    <span className="font-bold text-md mx-4">{selectedDate.year}</span>
                    <button onClick={() => onDateChange({year: selectedDate.year + 1, month: selectedDate.month})} className="p-1 hover:bg-gray-200 rounded-full"><ChevronRight size={18}/></button>
                </div>
                <div className="flex flex-wrap justify-center gap-1">
                     <button onClick={() => onDateChange({year: selectedDate.year, month: null})} className={`px-2 py-0.5 text-xs rounded-full ${selectedDate.month === null ? 'bg-yellow-500 text-white' : 'hover:bg-yellow-100/50'}`}>Tutti</button>
                    {monthNames.map((month, index) => (
                        <button key={index} onClick={() => onDateChange({year: selectedDate.year, month: index})} className={`px-2 py-0.5 text-xs rounded-full ${selectedDate.month === index ? 'bg-yellow-500 text-white' : 'hover:bg-yellow-100/50'}`}>{month}</button>
                    ))}
                </div>
            </div>
            
            <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
                <FilterButton label="Attivi" filter="active" count={filterCounts.active} currentFilter={orderStatusFilter} onFilterChange={onFilterChange} />
                <FilterButton label="Urgenti" filter="urgent" count={filterCounts.urgent} currentFilter={orderStatusFilter} onFilterChange={onFilterChange} icon={<Flame size={16}/>} />
                <FilterButton label="In Lavorazione" filter="In Lavorazione" count={filterCounts['In Lavorazione']} currentFilter={orderStatusFilter} onFilterChange={onFilterChange} />
                <FilterButton label="Pronti" filter="Pronto" count={filterCounts['Pronto']} currentFilter={orderStatusFilter} onFilterChange={onFilterChange} />
                <FilterButton label="Ritirati" filter="Ritirato" count={filterCounts['Ritirato']} currentFilter={orderStatusFilter} onFilterChange={onFilterChange} />
                <FilterButton label="Tutti" filter="all" count={filterCounts.all} currentFilter={orderStatusFilter} onFilterChange={onFilterChange} />
            </div>
            {isLoading && orders.length === 0 ? <p className="text-center text-gray-500 py-4">Caricamento ordini...</p> : (
            <>
                <div className="hidden md:block overflow-x-auto"><table className="w-full text-left"><thead className="border-b-2 border-gray-200"><tr><th className="p-3 text-sm font-semibold text-gray-500">Ordine #</th><th className="p-3 text-sm font-semibold text-gray-500">Cliente</th><th className="p-3 text-sm font-semibold text-gray-500">Capi</th><th className="p-3 text-sm font-semibold text-gray-500">Totale</th><th className="p-3 text-sm font-semibold text-gray-500">Stato</th><th className="p-3 text-sm font-semibold text-gray-500 text-center">Azioni</th></tr></thead><tbody>{displayedOrders.map(order => (<tr key={order.id} className={`border-b border-gray-100 hover:bg-yellow-50/50 ${order.priority ? 'border-l-4 border-red-500' : ''}`}><td className="p-4 font-bold">#{order.orderNumber}{order.priority && <Flame className="inline ml-2 text-red-500" size={16}/>}</td><td className="p-4"><div className="font-semibold" style={{color: COLORS.text}}>{order.clientName}</div><div className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString('it-IT')}</div></td><td className="p-4"><GroupedGarmentsList garments={order.garments} /></td><td className="p-4 font-semibold">€{order.total.toFixed(2)}</td><td className="p-4"><StatusBadge status={order.status} /></td><td className="p-4 text-center"><div className="inline-flex rounded-lg shadow-sm" role="group"><button onClick={() => onOpenEditOrderModal(order)} title="Modifica Ordine" className="px-3 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-l-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500"><Edit size={16} /></button><select value={order.status} onChange={(e) => onStatusChange(order.id, e.target.value)} className="px-3 py-2 text-sm font-medium text-gray-900 bg-white border-t border-b border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500 focus:outline-none">{Object.keys(STATUS_CONFIG).map(status => (<option key={status} value={status}>{status}</option>))}</select><button onClick={() => onPrintCustomerReceipt(order)} title="Stampa ricevuta cliente" className="px-3 py-2 text-sm font-medium text-gray-900 bg-white border-t border-b border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500"><Printer size={16} /></button><button onClick={() => onPrintHangerTicket(order)} title="Stampa etichetta gancio" className="px-3 py-2 text-sm font-medium text-gray-900 bg-white border-t border-b border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500"><Tag size={16}/></button>{order.status === 'Pronto' && clientPhoneMap[order.clientId] && (<button onClick={() => sendWhatsAppMessage(clientPhoneMap[order.clientId], order.clientName)} title="Notifica WhatsApp" className="px-3 py-2 text-sm font-medium text-green-600 bg-white border-t border-b border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500"><MessageSquare size={16} /></button>)}<button onClick={() => onDeleteRequest(order)} title="Elimina ordine" className="px-3 py-2 text-sm font-medium text-red-600 bg-white border border-gray-200 rounded-r-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500"><Trash2 size={16} /></button></div></td></tr>))}</tbody></table></div>
                <div className="md:hidden space-y-4">{displayedOrders.map(order => (<div key={order.id} className={`bg-gray-50 p-4 rounded-lg shadow-sm ${order.priority ? 'border-l-4 border-red-500' : ''}`}><div className="flex justify-between items-start"><div><p className="font-bold" style={{color: COLORS.text}}>{order.clientName}</p><p className="text-sm font-semibold">Ordine #{order.orderNumber} - €{order.total.toFixed(2)}</p><p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString('it-IT')}</p></div><StatusBadge status={order.status} /></div><GroupedGarmentsList garments={order.garments} /><div className="flex justify-end flex-wrap gap-2 mt-2"><button onClick={() => onOpenEditOrderModal(order)} title="Modifica Ordine" className="p-2 text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-100"><Edit size={16} /></button><select value={order.status} onChange={(e) => onStatusChange(order.id, e.target.value)} className="p-2 text-xs font-medium text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-yellow-500 focus:outline-none">{Object.keys(STATUS_CONFIG).map(status => (<option key={status} value={status}>{status}</option>))}</select><button onClick={() => onPrintCustomerReceipt(order)} title="Stampa ricevuta cliente" className="p-2 text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-100"><Printer size={16} /></button><button onClick={() => onPrintHangerTicket(order)} title="Stampa etichetta gancio" className="p-2 text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-100"><Tag size={16}/></button>{order.status === 'Pronto' && clientPhoneMap[order.clientId] && (<button onClick={() => sendWhatsAppMessage(clientPhoneMap[order.clientId], order.clientName)} title="Notifica WhatsApp" className="p-2 text-green-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100"><MessageSquare size={16} /></button>)}<button onClick={() => onDeleteRequest(order)} title="Elimina ordine" className="p-2 text-red-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100"><Trash2 size={16} /></button></div></div>))}</div>
                {hasMore && (
                    <div className="mt-6 text-center">
                        <button onClick={onLoadMore} disabled={isLoading} className="px-6 py-2 text-sm font-semibold text-white bg-yellow-500 rounded-lg shadow-md hover:bg-yellow-600 disabled:bg-gray-400">
                            {isLoading ? 'Caricamento...' : 'Carica Altri'}
                        </button>
                    </div>
                )}
            </>
            )}
        </div>
    );
};

const ClientsView = ({ clients, onEditClient, onOpenNewClientModal, onDeleteClient, onOpenClientDetail }) => (
    <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-md border border-white/20">
        <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold font-display" style={{color: COLORS.text}}>Elenco Clienti</h2><button onClick={onOpenNewClientModal} className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-white font-semibold shadow-md transition-transform hover:scale-105 bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500"><Plus size={16}/>Nuovo Cliente</button></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{clients.map(client => (<div key={client.id} className="bg-white/50 p-4 border rounded-lg hover:shadow-lg transition-shadow relative cursor-pointer" style={{borderColor: COLORS.secondary}} onClick={() => onOpenClientDetail(client)}>
            <div className="absolute top-3 right-3 flex gap-2">
                <button onClick={(e) => { e.stopPropagation(); onEditClient(client); }} className="text-gray-400 hover:text-yellow-600 transition-colors" title="Modifica Cliente"><Edit size={18}/></button>
                <button onClick={(e) => { e.stopPropagation(); onDeleteClient(client); }} className="text-gray-400 hover:text-red-600 transition-colors" title="Elimina Cliente"><Trash2 size={18}/></button>
            </div>
            <h3 className="font-bold text-lg pr-16" style={{color: COLORS.primary}}>{client.name}</h3>
            <p className="text-sm text-gray-600">Tel: {client.phone || 'Non fornito'}</p><p className="text-sm text-gray-600">Email: {client.email || 'Non fornita'}</p><p className="text-xs text-gray-400 mt-2">Cliente dal: {new Date(client.joinDate).toLocaleDateString('it-IT')}</p></div>))}</div>
    </div>
);

const SettingsView = ({ onExportData, onRestoreDataClick, onLogout, navigateTo, currentUser, onClearDataRequest }) => {
    return (
        <div className="space-y-6">
             <div className="bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-md border border-white/20">
                 <p className="text-gray-600 text-center">Connesso come: <span className="font-semibold" style={{color: COLORS.primary}}>{currentUser?.email}</span></p>
             </div>
            <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-md border border-white/20">
                <button onClick={() => navigateTo('services')} className="w-full flex justify-between items-center text-left p-4 rounded-lg hover:bg-gray-100/50">
                    <div><h3 className="text-lg font-semibold" style={{color: COLORS.text}}>Listino Prezzi</h3><p className="text-sm text-gray-500">Gestisci i tuoi servizi e i prezzi predefiniti.</p></div>
                    <ChevronRight size={20} className="text-gray-400" />
                </button>
            </div>
            <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-md border border-white/20">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <h3 className="text-lg font-semibold" style={{color: COLORS.text}}>Backup & Ripristino</h3>
                    <div className="flex gap-2">
                        <button onClick={onRestoreDataClick} className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-white font-semibold shadow-sm transition-transform hover:scale-105 bg-gradient-to-r from-amber-600 to-amber-500"><Upload size={16}/> Ripristina</button>
                        <button onClick={onExportData} className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-white font-semibold shadow-sm transition-transform hover:scale-105 bg-gradient-to-r from-sky-600 to-sky-500"><Download size={16}/> Esporta</button>
                    </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center sm:text-left">Il ripristino sovrascriverà tutti i dati attuali con quelli del file di backup.</p>
            </div>
            <div className="bg-red-50 border border-red-200 p-4 md:p-6 rounded-xl shadow-md space-y-4">
                <h3 className="text-lg font-bold text-red-700">Azioni Irreversibili</h3>
                <div>
                    <p className="text-sm text-orange-600 my-2">Questa operazione cancellerà tutti gli ordini e i dati contabili. Clienti e listino prezzi verranno conservati.</p>
                    <button onClick={() => onClearDataRequest('orders_only')} className="w-full flex justify-center items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold shadow-md transition-transform hover:scale-105 bg-orange-500 hover:bg-orange-600">
                        <Trash2 size={16}/> Cancella Ordini e Dati Contabili
                    </button>
                </div>
                <div className="border-t border-red-200 pt-4">
                    <p className="text-sm text-red-600 my-2">Questa operazione cancellerà TUTTI i dati dell'applicazione, inclusi clienti e servizi. Usare con estrema cautela.</p>
                    <button onClick={() => onClearDataRequest('all')} className="w-full flex justify-center items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold shadow-md transition-transform hover:scale-105 bg-red-600 hover:bg-red-700">
                        <AlertTriangle size={16}/> Cancella Tutti i Dati
                    </button>
                </div>
            </div>
             <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-md border border-white/20">
                <button onClick={onLogout} className="w-full flex justify-center items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold shadow-md transition-transform hover:scale-105 bg-gray-700 hover:bg-gray-800"><LogOut size={16}/> Esci dall'Applicazione</button>
             </div>
             <div className="text-center text-sm text-gray-400 pt-8">
                 <p className="font-semibold">GestionaleStrasso rel v{VERSION}</p>
                 <p>Un’app nata da un’idea di <strong style={{color: COLORS.primary}}>Erika Cesarano</strong> <Moon size={14} className="inline-block text-yellow-500" /></p>
             </div>
        </div>
    );
}

const ServicesView = ({ services, onAddService, onUpdateService, onDeleteService }) => {
    const [name, setName] = useState('');
    const [washAndIronPrice, setWashAndIronPrice] = useState('');
    const [washOnlyPrice, setWashOnlyPrice] = useState('');
    const [ironOnlyPrice, setIronOnlyPrice] = useState('');
    const [dryCleaningPrice, setDryCleaningPrice] = useState('');
    const [editingService, setEditingService] = useState(null);

    const handleAdd = () => {
        if (name) {
            onAddService({
                name,
                washAndIronPrice: parseFloat(washAndIronPrice || 0),
                washOnlyPrice: parseFloat(washOnlyPrice || 0),
                ironOnlyPrice: parseFloat(ironOnlyPrice || 0),
                dryCleaningPrice: parseFloat(dryCleaningPrice || 0)
            });
            setName('');
            setWashAndIronPrice('');
            setWashOnlyPrice('');
            setIronOnlyPrice('');
            setDryCleaningPrice('');
        }
    };

    const handleEdit = (service) => {
        setEditingService({ ...service });
    };

    const handleSave = () => {
        if (editingService) {
            onUpdateService(editingService.id, {
                name: editingService.name,
                washAndIronPrice: parseFloat(editingService.washAndIronPrice || 0),
                washOnlyPrice: parseFloat(editingService.washOnlyPrice || 0),
                ironOnlyPrice: parseFloat(editingService.ironOnlyPrice || 0),
                dryCleaningPrice: parseFloat(editingService.dryCleaningPrice || 0)
            });
            setEditingService(null);
        }
    };
    
    const handleCancelEdit = () => {
        setEditingService(null);
    }

    return (
        <div className="bg-white/90 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-md border border-white/20 space-y-6">
            <div>
                <h2 className="text-2xl font-bold font-display" style={{color: COLORS.text}}>Listino Prezzi</h2>
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mt-4 p-4 border rounded-lg bg-gray-50/50">
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nome Capo (es. Piumone)" className="lg:col-span-2 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"/>
                    <input type="number" step="0.01" value={washAndIronPrice} onChange={(e) => setWashAndIronPrice(e.target.value)} placeholder="Lavaggio+Stiro (€)" className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"/>
                    <input type="number" step="0.01" value={washOnlyPrice} onChange={(e) => setWashOnlyPrice(e.target.value)} placeholder="Solo Lavaggio (€)" className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"/>
                    <input type="number" step="0.01" value={ironOnlyPrice} onChange={(e) => setIronOnlyPrice(e.target.value)} placeholder="Solo Stiro (€)" className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"/>
                    <input type="number" step="0.01" value={dryCleaningPrice} onChange={(e) => setDryCleaningPrice(e.target.value)} placeholder="Lav. a Secco (€)" className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"/>
                    <button onClick={handleAdd} className="lg:col-span-6 px-4 py-2 text-white font-semibold rounded-md shadow-md bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500">Aggiungi Capo</button>
                </div>
            </div>
            <div className="space-y-2">
                {services.map(service => (
                    <div key={service.id} className="flex flex-col md:flex-row justify-between md:items-center bg-white/50 p-3 rounded-lg gap-2">
                        {editingService?.id === service.id ? (
                            <>
                                <input type="text" value={editingService.name || ''} onChange={e => setEditingService({...editingService, name: e.target.value})} className="font-semibold border-b-2 border-yellow-500 p-1"/>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <input type="number" step="0.01" value={editingService.washAndIronPrice || ''} onChange={e => setEditingService({...editingService, washAndIronPrice: e.target.value})} className="text-sm text-gray-500 w-28 border-b-2 border-yellow-500 p-1" placeholder="Lavaggio+Stiro"/>
                                    <input type="number" step="0.01" value={editingService.washOnlyPrice || ''} onChange={e => setEditingService({...editingService, washOnlyPrice: e.target.value})} className="text-sm text-gray-500 w-28 border-b-2 border-yellow-500 p-1" placeholder="Solo Lavaggio"/>
                                    <input type="number" step="0.01" value={editingService.ironOnlyPrice || ''} onChange={e => setEditingService({...editingService, ironOnlyPrice: e.target.value})} className="text-sm text-gray-500 w-28 border-b-2 border-yellow-500 p-1" placeholder="Solo Stiro"/>
                                    <input type="number" step="0.01" value={editingService.dryCleaningPrice || ''} onChange={e => setEditingService({...editingService, dryCleaningPrice: e.target.value})} className="text-sm text-gray-500 w-28 border-b-2 border-yellow-500 p-1" placeholder="A Secco"/>
                                    <button onClick={handleSave} className="text-green-600 hover:text-green-800"><Save size={18}/></button>
                                    <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700"><X size={18}/></button>
                                </div>
                            </>
                        ) : (
                            <>
                                <span className="font-semibold text-lg md:text-base">{service.name}</span>
                                <div className="flex items-center gap-4 flex-wrap text-xs">
                                    <span>L+S: <strong>€{(service.washAndIronPrice || 0).toFixed(2)}</strong></span>
                                    <span>Solo L: <strong>€{(service.washOnlyPrice || 0).toFixed(2)}</strong></span>
                                    <span>Solo S: <strong>€{(service.ironOnlyPrice || 0).toFixed(2)}</strong></span>
                                    <span>Secco: <strong>€{(service.dryCleaningPrice || 0).toFixed(2)}</strong></span>
                                    <button onClick={() => handleEdit(service)} className="text-gray-400 hover:text-yellow-600"><Edit size={16}/></button>
                                    <button onClick={() => onDeleteService(service.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16}/></button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const ClientDetailModal = ({ isOpen, onClose, client, clientHistory, services }) => {
    
    const clientStats = useMemo(() => {
        if (!client || !clientHistory || clientHistory.length === 0) {
            return {
                avgSpend: 0,
                frequency: 'N/A',
                topServices: [],
                commercialInsight: 'Nessuna cronologia ordini per generare un consiglio.'
            };
        }

        const totalSpent = clientHistory.reduce((sum, order) => sum + order.total, 0);
        const avgSpend = totalSpent / clientHistory.length;

        const sortedDates = clientHistory.map(o => new Date(o.createdAt)).sort((a, b) => a - b);
        const daysBetween = sortedDates.length > 1
            ? (sortedDates[sortedDates.length - 1] - sortedDates[0]) / (1000 * 60 * 60 * 24) / (sortedDates.length - 1)
            : 'N/A';

        const frequency = typeof daysBetween === 'number' ? `Ogni ${Math.round(daysBetween)} giorni` : "Cliente nuovo";

        const serviceCounts = clientHistory
            .flatMap(order => order.garments)
            .reduce((acc, garment) => {
                acc[garment.name] = (acc[garment.name] || 0) + 1;
                return acc;
            }, {});

        const topServices = Object.entries(serviceCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(item => item[0]);

        let commercialInsight = "Offri uno sconto del 10% sul prossimo ordine per fidelizzare il cliente.";
        if (topServices.length > 0) {
            const topService = topServices[0];
            const relatedService = services.find(s => s.name.includes(topService.split(" ")[0]) && s.name !== topService);
            if(relatedService) {
                commercialInsight = `Dato che richiede spesso "${topService}", potresti proporgli uno sconto su "${relatedService.name}".`;
            }
        }

        return { avgSpend, frequency, topServices, commercialInsight };
    }, [client, clientHistory, services]);
    
    if (!isOpen || !client) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Profilo Cliente: ${client.name}`}>
            <div className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-bold text-lg mb-2" style={{color: COLORS.primary}}>Abitudini Cliente</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-gray-500">Spesa Media</p>
                            <p className="font-semibold text-lg">€{clientStats.avgSpend.toFixed(2)}</p>
                        </div>
                        <div>
                            <p className="text-gray-500">Frequenza</p>
                            <p className="font-semibold text-lg">{clientStats.frequency}</p>
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-bold text-lg mb-2" style={{color: COLORS.primary}}>Servizi Preferiti</h3>
                    {clientStats.topServices.length > 0 ? (
                        <ol className="list-decimal list-inside space-y-1 text-sm">
                            {clientStats.topServices.map((service, i) => <li key={i}>{service}</li>)}
                        </ol>
                    ) : <p className="text-sm text-gray-500">Nessun dato sufficiente.</p>}
                </div>
                
                <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                     <h3 className="font-bold text-lg mb-2 flex items-center text-blue-800"><Lightbulb className="mr-2" size={20}/>Consiglio Commerciale</h3>
                     <p className="text-sm text-blue-700">{clientStats.commercialInsight}</p>
                </div>
            </div>
        </Modal>
    );
};

const BottomNav = ({ activeView, navigateTo, isVisible }) => (
    <div className={`fixed bottom-0 left-0 z-40 w-full h-14 bg-white/80 backdrop-blur-lg border-t border-white/30 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] transition-transform duration-300 ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="grid h-full max-w-lg grid-cols-4 mx-auto font-medium">
            <button onClick={() => navigateTo('dashboard')} type="button" className={`inline-flex flex-col items-center justify-center px-5 hover:bg-gray-50 group ${activeView === 'dashboard' ? 'text-yellow-600' : 'text-gray-500'}`}><BarChart2 className="w-5 h-5 mb-1" /><span className="text-xs">Dashboard</span></button>
            <button onClick={() => navigateTo('orders')} type="button" className={`inline-flex flex-col items-center justify-center px-5 hover:bg-gray-50 group ${activeView === 'orders' ? 'text-yellow-600' : 'text-gray-500'}`}><Package className="w-5 h-5 mb-1" /><span className="text-xs">Ordini</span></button>
            <button onClick={() => navigateTo('clients')} type="button" className={`inline-flex flex-col items-center justify-center px-5 hover:bg-gray-50 group ${activeView === 'clients' ? 'text-yellow-600' : 'text-gray-500'}`}><Users className="w-5 h-5 mb-1" /><span className="text-xs">Clienti</span></button>
            <button onClick={() => navigateTo('settings')} type="button" className={`inline-flex flex-col items-center justify-center px-5 hover:bg-gray-50 group ${activeView === 'settings' ? 'text-yellow-600' : 'text-gray-500'}`}><Settings className="w-5 h-5 mb-1" /><span className="text-xs">Impostazioni</span></button>
        </div>
    </div>
);

const AuthScreen = ({ onLogin, onRegister, onSwitchToTracking }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [error, setError] = useState('');
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            if (isRegistering) { await onRegister(email, password); } else { await onLogin(email, password); }
        } catch(err) {
            switch(err.code) {
                case 'auth/email-already-in-use': setError('Questa email è già registrata. Prova ad accedere.'); break;
                case 'auth/weak-password': setError('La password deve essere di almeno 6 caratteri.'); break;
                case 'auth/invalid-email': setError('Formato email non valido.'); break;
                case 'auth/user-not-found': case 'auth/wrong-password': case 'auth/invalid-credential': setError('Credenziali non corrette.'); break;
                default: setError('Si è verificato un errore. Riprova.'); break;
            }
            console.error(err);
        }
    }
    return (
        <div className="w-screen h-screen flex justify-center items-center bg-gradient-to-br from-gray-50 to-gray-200 p-4">
            <div className="w-full max-w-sm mx-auto bg-white/80 backdrop-blur-lg p-8 rounded-xl shadow-2xl text-center border border-white/30">
                <img src="https://i.imgur.com/ANHwUR3.png" alt="Logo" className="h-20 object-contain mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-6" style={{color: COLORS.text}}>{isRegistering ? "Registra Nuovo Account" : "Accesso Gestionale"}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min. 6 caratteri)" required className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button type="submit" className="w-full flex justify-center items-center gap-2 px-4 py-3 rounded-lg text-white font-semibold shadow-md transition-transform hover:scale-105 bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500">
                        {isRegistering ? <UserPlus size={20} /> : <LogIn size={20} />}
                        {isRegistering ? "Registrati" : "Accedi"}
                    </button>
                </form>
                <button onClick={() => setIsRegistering(!isRegistering)} className="text-sm text-gray-600 hover:text-yellow-600 mt-6">{isRegistering ? "Hai già un account? Accedi" : "Non hai un account? Registrati"}</button>
                <div className="my-4 border-t border-gray-200"></div>
                <button onClick={onSwitchToTracking} className="text-sm text-blue-600 hover:text-blue-800 font-semibold">Traccia il tuo ordine</button>
            </div>
        </div>
    )
};

const TrackingView = ({ onBackToLogin }) => {
    const [orderId, setOrderId] = useState('');
    const [orderStatus, setOrderStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    const handleSearch = async (idToSearch) => {
        if(!idToSearch || !idToSearch.trim()) return;
        setIsLoading(true); setError(''); setOrderStatus(null);
        try {
            const q = query(collection(db, "artifacts", appId, "public_orders"), where("orderNumber", "==", parseInt(idToSearch.trim())));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const orderDoc = querySnapshot.docs[0];
                setOrderStatus(orderDoc.data().status);
            } else { setError("Ordine non trovato. Controlla il codice e riprova."); }
        } catch (err) { setError("Si è verificato un errore durante la ricerca."); console.error(err);
        } finally { setIsLoading(false); }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        handleSearch(orderId);
    }
    
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const orderNumberFromUrl = params.get('orderNumber');
        if (orderNumberFromUrl) {
            setOrderId(orderNumberFromUrl);
            handleSearch(orderNumberFromUrl);
        }
    }, []);

    return (
        <div className="w-screen h-screen flex justify-center items-center bg-gradient-to-br from-gray-50 to-gray-200 p-4">
             <div className="w-full max-w-sm mx-auto bg-white/80 backdrop-blur-lg p-8 rounded-xl shadow-2xl text-center border border-white/30">
                <img src="https://i.imgur.com/ANHwUR3.png" alt="Logo" className="h-20 object-contain mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-6" style={{color: COLORS.text}}>Stato del Tuo Ordine</h2>
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    <input type="text" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Inserisci ID Ordine" required className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" />
                    <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center gap-2 px-4 py-3 rounded-lg text-white font-semibold shadow-md transition-transform hover:scale-105 bg-gradient-to-r from-sky-600 to-sky-500 disabled:opacity-50">
                        {isLoading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <Search size={20} />} Cerca Ordine
                    </button>
                </form>
                {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                {orderStatus && (
                    <div className="mt-6 p-4 rounded-lg" style={{backgroundColor: STATUS_CONFIG[orderStatus]?.bg || '#eee'}}>
                        <p className="font-semibold text-lg">Stato attuale:</p>
                        <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1 text-lg font-semibold rounded-full ${STATUS_CONFIG[orderStatus]?.bg} ${STATUS_CONFIG[orderStatus]?.text}`}>
                            {STATUS_CONFIG[orderStatus]?.icon} {orderStatus}
                        </div>
                    </div>
                )}
                 <button onClick={onBackToLogin} className="text-sm text-gray-600 hover:text-yellow-600 mt-6 flex items-center gap-1 mx-auto">
                     <ChevronLeft size={16} /> Torna al Login
                 </button>
             </div>
        </div>
    )
}

const NewOrderModal = ({ isOpen, onClose, clients, services, onAddOrder, onUpdateOrder, editingOrder }) => {
    const initialGarmentState = { id: Date.now(), name: '', price: '', notes: '', serviceType: 'wash_iron', quantity: 1, isCustom: false };
    const initialOrderState = { clientName: '', clientPhone: '', clientEmail: '', garments: [initialGarmentState], priority: false, dueDate: '' };

    const [orderData, setOrderData] = useState(initialOrderState);
    const [selectedClientId, setSelectedClientId] = useState("");
    const [clientSearch, setClientSearch] = useState("");
    const [useDueDate, setUseDueDate] = useState(true);

    const getDefaultDueDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 3);
        return date.toISOString().split('T')[0];
    };

    useEffect(() => {
        if (isOpen) {
            if(editingOrder) {
                setOrderData({
                    ...initialOrderState,
                    ...editingOrder,
                    dueDate: editingOrder.dueDate || getDefaultDueDate()
                });
                setSelectedClientId(editingOrder.clientId);
                setUseDueDate(!!editingOrder.dueDate);
                const client = clients.find(c => c.id === editingOrder.clientId);
                if(client) { setClientSearch(client.name); }
            } else {
                setOrderData({ ...initialOrderState, dueDate: getDefaultDueDate() });
                setSelectedClientId('');
                setClientSearch('');
                setUseDueDate(true);
            }
        }
    }, [editingOrder, isOpen, clients]);
    
    const handleClose = () => {
        onClose();
    };

    const handleClientSelection = (clientId) => {
        setSelectedClientId(clientId);
        if (clientId) {
            const client = clients.find(c => c.id === clientId);
            if (client) {
                setOrderData(prev => ({ ...prev, clientName: client.name, clientPhone: client.phone || '', clientEmail: client.email || '' }));
                 setClientSearch(client.name);
            }
        } else {
             setOrderData(prev => ({ ...prev, clientName: clientSearch, clientPhone: '', clientEmail: '' }));
        }
    };
    
   const recalculateGarmentPrice = (garment) => {
        if (!garment.name || garment.isCustom) return garment.price;
        const service = services.find(s => s.name === garment.name);
        if (!service) return 0;

        switch (garment.serviceType) {
            case 'wash_iron':
                return (service.washAndIronPrice || 0);
            case 'wash_only':
                return service.washOnlyPrice || 0;
            case 'iron_only':
                return service.ironOnlyPrice || 0;
            case 'dry_clean':
                return service.dryCleaningPrice || 0;
            default:
                return 0;
        }
    };
    
    const handleGarmentAttributeChange = (index, field, value) => {
        const updatedGarments = [...orderData.garments];
        let newGarmentState = { ...updatedGarments[index], [field]: value };
        
        if (field !== 'price') {
             newGarmentState.price = recalculateGarmentPrice(newGarmentState);
        }

        updatedGarments[index] = newGarmentState;
        
        const total = updatedGarments.reduce((sum, g) => sum + (parseFloat(g.price) || 0) * (parseInt(g.quantity, 10) || 1), 0);
        setOrderData({...orderData, garments: updatedGarments, total: total});
    };
    
    const addGarmentField = () => setOrderData(prev => ({ ...prev, garments: [...prev.garments, { ...initialGarmentState, id: Date.now() }] }));
    const removeGarmentField = (index) => { const updatedGarments = orderData.garments.filter((_, i) => i !== index); setOrderData({...orderData, garments: updatedGarments});};
    
    const handleServiceSelection = (index, selectedValue) => {
        const updatedGarments = [...orderData.garments];
        let updatedGarment;

        if (selectedValue === 'custom') {
            updatedGarment = {
                ...initialGarmentState,
                name: '',
                isCustom: true,
                price: '',
                serviceType: 'custom_service'
            };
        } else {
            updatedGarment = { ...updatedGarments[index], name: selectedValue, isCustom: false };
            updatedGarment.price = recalculateGarmentPrice(updatedGarment);
        }

        updatedGarments[index] = updatedGarment;
        
        const total = updatedGarments.reduce((sum, g) => sum + (g.price || 0) * (parseInt(g.quantity, 10) || 1), 0);
        setOrderData({...orderData, garments: updatedGarments, total: total});
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();

        const expandedGarments = orderData.garments.flatMap(g => {
            const qty = parseInt(g.quantity, 10) || 1;
            
            let serviceDescription = '';
             if (g.isCustom) {
                 serviceDescription = 'Personalizzato';
             } else {
                 switch(g.serviceType){
                     case 'wash_iron': serviceDescription = 'Lavaggio + Stiro'; break;
                     case 'wash_only': serviceDescription = 'Solo Lavaggio'; break;
                     case 'iron_only': serviceDescription = 'Solo Stiro'; break;
                     case 'dry_clean': serviceDescription = 'Lavaggio a Secco'; break;
                     case 'custom_service': serviceDescription = 'Servizio Personalizzato'; break;
                     default: serviceDescription = '';
                 }
             }

            return Array.from({ length: qty }, () => ({
                name: g.name,
                price: parseFloat(g.price || 0),
                notes: g.notes || '',
                serviceDescription: serviceDescription,
            }));
        });
        
        const total = expandedGarments.reduce((sum, g) => sum + g.price, 0);
        
        const finalPayload = {
            clientName: selectedClientId ? clients.find(c=>c.id === selectedClientId).name : clientSearch,
            clientPhone: orderData.clientPhone || '',
            clientEmail: orderData.clientEmail || '',
            garments: expandedGarments,
            priority: orderData.priority || false,
            dueDate: useDueDate ? orderData.dueDate : null,
            total,
            status: 'In Lavorazione',
            createdAt: new Date().toISOString()
        };

        if(editingOrder) {
            const updatePayload = {
                ...finalPayload,
                orderNumber: editingOrder.orderNumber,
                createdAt: editingOrder.createdAt,
                status: editingOrder.status || 'In Lavorazione', 
            };
            onUpdateOrder(editingOrder.id, updatePayload);
        } else {
            onAddOrder(finalPayload, selectedClientId);
        }
        handleClose();
    };
    const filteredClients = useMemo(() => clientSearch ? clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())) : [], [clients, clientSearch]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={editingOrder ? `Modifica Ordine #${editingOrder.orderNumber}` : "Nuovo Ordine"}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Cerca o Seleziona Cliente</label>
                    <input
                        type="text"
                        value={clientSearch || ''}
                        onChange={(e) => {
                            setClientSearch(e.target.value);
                            setSelectedClientId("");
                            setOrderData(prev => ({...prev, clientName: e.target.value, clientPhone: '', clientEmail: ''}));
                        }}
                        placeholder="Inizia a scrivere per cercare o creare..."
                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                    />
                    {filteredClients.length > 0 && !selectedClientId && (
                        <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-40 overflow-y-auto">
                            {filteredClients.map(c => (
                                <li key={c.id} onClick={() => handleClientSelection(c.id)} className="px-3 py-2 hover:bg-yellow-100 cursor-pointer">{c.name}</li>
                            ))}
                        </ul>
                    )}
                </div>
                
                {selectedClientId ?
                    <div className="border-t pt-4">
                        <p className="text-sm text-gray-600">Cliente selezionato: <span className="font-bold text-green-600">{orderData.clientName}</span></p>
                    </div> :
                    (<div className="border-t pt-4 space-y-4">
                        <p className="text-sm text-gray-500 text-center">Stai creando un nuovo cliente</p>
                        <div><label className="block text-sm font-medium text-gray-700">Telefono (per WhatsApp)</label><input type="tel" value={orderData.clientPhone || ''} onChange={(e) => setOrderData({...orderData, clientPhone: e.target.value})} placeholder="Es. +39 3331234567" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Email Cliente</label><input type="email" value={orderData.clientEmail || ''} onChange={(e) => setOrderData({...orderData, clientEmail: e.target.value})} placeholder="Es. mario.rossi@email.com" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                    </div>)
                }
                
                <div className="flex items-center gap-4 pt-4 border-t">
                    <input type="checkbox" id="dueDateToggle" checked={useDueDate} onChange={e => setUseDueDate(e.target.checked)} className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500" />
                    <label htmlFor="dueDateToggle" className="text-sm font-medium text-gray-700">Imposta data di consegna</label>
                </div>
                {useDueDate && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Data di Consegna Prevista</label>
                        <input type="date" value={orderData.dueDate || ''} onChange={(e) => setOrderData({...orderData, dueDate: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" />
                    </div>
                )}
                <div className="flex items-center">
                    <input type="checkbox" id="priority" checked={orderData.priority || false} onChange={e => setOrderData({...orderData, priority: e.target.checked})} className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500" />
                    <label htmlFor="priority" className="ml-2 block text-sm text-red-600 font-bold">Ordine Urgente</label>
                </div>
                <h4 className="text-lg font-semibold pt-4 border-t">Capi</h4>
                {orderData.garments.map((garment, index) => (
                    <div key={garment.id || index} className="p-3 border rounded-md space-y-3 bg-gray-50 relative">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <select onChange={(e) => handleServiceSelection(index, e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" value={garment.isCustom ? 'custom' : garment.name || ''}>
                                <option value="">-- Seleziona Capo --</option>
                                {services.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                <option value="custom" className="font-bold text-blue-600">--- Articolo Personalizzato ---</option>
                            </select>
                            <input type="number" value={garment.quantity || 1} onChange={(e) => handleGarmentAttributeChange(index, 'quantity', e.target.value)} placeholder="Quantità" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" min="1"/>
                        </div>

                        {garment.isCustom ? (
                            <div className="space-y-3 pt-2 border-t border-dashed">
                               <input
                                    type="text"
                                    placeholder="Nome articolo personalizzato (es. Tenda)"
                                    value={garment.name || ''}
                                    onChange={(e) => handleGarmentAttributeChange(index, 'name', e.target.value)}
                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                                />
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Prezzo Personalizzato (€)"
                                    value={garment.price ?? ''}
                                    onChange={(e) => handleGarmentAttributeChange(index, 'price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                                />
                            </div>
                        ) : (
                             <div className="space-y-3">
                                 <div className="flex gap-4 items-center flex-wrap">
                                     <select value={garment.serviceType} onChange={(e) => handleGarmentAttributeChange(index, 'serviceType', e.target.value)} className="flex-grow px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500">
                                         <option value="wash_iron">Lavaggio + Stiro</option>
                                         <option value="wash_only">Solo Lavaggio</option>
                                         <option value="iron_only">Solo Stiro</option>
                                         <option value="dry_clean">Lavaggio a Secco</option>
                                         <option value="custom_service">Personalizzato</option>
                                     </select>
                                </div>
                                <input
                                    type="number" step="0.01" placeholder="Prezzo (€)"
                                    value={garment.price ?? ''}
                                    onChange={(e) => handleGarmentAttributeChange(index, 'price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                                />
                             </div>
                        )}
                        <textarea placeholder={garment.isCustom ? "Descrivere i dettagli del servizio personalizzato qui..." : "Note sul capo (es. colletto macchiato)..."} value={garment.notes || ''} onChange={(e) => handleGarmentAttributeChange(index, 'notes', e.target.value)} rows="2" className="w-full mt-2 px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" />
                        {orderData.garments.length > 1 && ( <button type="button" onClick={() => removeGarmentField(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"> <X size={16} /> </button> )}
                    </div>
                ))}
                <button type="button" onClick={addGarmentField} className="text-sm flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold"> <Plus size={16} /> Aggiungi capo </button>
                <div className="pt-4 border-t flex justify-end"><button type="submit" className="px-6 py-2 rounded-md text-white font-semibold transition-colors bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500"> Salva </button></div>
            </form>
        </Modal>
    );
};

const NewClientModal = ({ isOpen, onClose, onAddClient }) => {
    const [name, setName] = useState(''); const [phone, setPhone] = useState(''); const [email, setEmail] = useState('');
    const [joinDate, setJoinDate] = useState(new Date().toISOString().split('T')[0]);
    const [birthDate, setBirthDate] = useState('');

    const handleSubmit = () => {
        if(!name) { console.warn("Il nome del cliente è obbligatorio."); return; }
        onAddClient({ name, phone, email, joinDate, birthDate });
        setName(''); setPhone(''); setEmail(''); setJoinDate(new Date().toISOString().split('T')[0]); setBirthDate('');
        onClose();
    }
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Nuovo Cliente">
             <div className="space-y-4">
                 <div><label className="block text-sm font-medium text-gray-700">Nome Cliente</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Mario Rossi" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                 <div><label className="block text-sm font-medium text-gray-700">Telefono</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Es. +39 3331234567" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                 <div><label className="block text-sm font-medium text-gray-700">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Es. mario.rossi@email.com" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                 <div><label className="block text-sm font-medium text-gray-700">Data di Nascita (opzionale)</label><input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                 <div><label className="block text-sm font-medium text-gray-700">Data Iscrizione</label><input type="date" value={joinDate} onChange={(e) => setJoinDate(e.target.value)} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                 <div className="pt-4 border-t flex justify-end"><button onClick={handleSubmit} className="px-6 py-2 rounded-md text-white font-semibold transition-colors" style={{ backgroundColor: COLORS.primary }}> Salva Cliente </button></div>
             </div>
        </Modal>
    );
}

// --- MAIN APP COMPONENT ---
function LaundryApp({ currentUser, onLogout, activeView, setActiveView }) {
  // STATI DATI
  const [allOrders, setAllOrders] = useState([]); 
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  // STATI PER LA VISTA ORDINI (PAGINAZIONE)
  const [orders, setOrders] = useState([]); 
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [lastOrderSnapshot, setLastOrderSnapshot] = useState(null);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);

  // STATI MODALI E FORM
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirmDeleteClientModalOpen, setIsConfirmDeleteClientModalOpen] = useState(false);
  const [isRestoreConfirmModalOpen, setIsRestoreConfirmModalOpen] = useState(false);
  const [isPasswordConfirmOpen, setIsPasswordConfirmOpen] = useState({ open: false, type: '' });
  const [passwordError, setPasswordError] = useState('');
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [dataToRestore, setDataToRestore] = useState(null);
  const fileInputRef = useRef(null);

  // STATI FILTRI E UI
  const [searchTerm, setSearchTerm] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('active'); 
  const [selectedDate, setSelectedDate] = useState({ year: new Date().getFullYear(), month: null });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [orderForCustomerReceipt, setOrderForCustomerReceipt] = useState(null);
  const [orderForHangerTicket, setOrderForHangerTicket] = useState(null);
  const [isClientDetailModalOpen, setIsClientDetailModalOpen] = useState(false);
  const [selectedClientForDetail, setSelectedClientForDetail] = useState(null);
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const lastScrollY = useRef(0);
  const mainContentRef = useRef(null);

  // STATO PER I CONTEGGI
  const [filterCounts, setFilterCounts] = useState({ active: 0, urgent: 0, 'In Lavorazione': 0, 'Pronto': 0, 'Ritirato': 0, all: 0 });

  // Effetto per nascondere/mostrare la BottomNav allo scroll
  useEffect(() => {
    const handleScroll = () => {
      if (mainContentRef.current) {
        const currentScrollY = mainContentRef.current.scrollTop;
        if (currentScrollY > lastScrollY.current && currentScrollY > 20) {
          setIsBottomNavVisible(false);
        } else {
          setIsBottomNavVisible(true);
        }
        lastScrollY.current = currentScrollY;
      }
    };
    const contentEl = mainContentRef.current;
    if (contentEl) {
        contentEl.addEventListener('scroll', handleScroll, { passive: true });
        return () => contentEl.removeEventListener('scroll', handleScroll);
    }
  }, []); 
  
  // Effetto per stato online/offline (sia via eventi che via polling per robustezza)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const checkStatus = async () => {
        try {
            await fetch(`https://i.imgur.com/ANHwUR3.png?t=${new Date().getTime()}`, { method: 'HEAD', mode: 'no-cors', cache: 'no-cache' });
            setIsOnline(true);
        } catch (error) {
            setIsOnline(false);
        }
    };
    checkStatus();
    const intervalId = setInterval(checkStatus, 5000); 

    return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        clearInterval(intervalId);
    };
}, []);


  // Listener dati non paginati (clienti, servizi, transazioni, e tutti gli ordini per le stats)
  useEffect(() => {
    if (!currentUser || !db) return;
    const userId = currentUser.uid;
    const ordersCol = collection(db, "artifacts", appId, "users", userId, "orders");
    const clientsCol = collection(db, "artifacts", appId, "users", userId, "clients");
    const servicesCol = collection(db, "artifacts", appId, "users", userId, "services");
    const transactionsCol = collection(db, "artifacts", appId, "users", userId, "transactions");
    
    const unsubAllOrders = onSnapshot(query(ordersCol), snapshot => {
        setAllOrders(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    }, (error) => console.error("All Orders snapshot error:", error));

    const unsubClients = onSnapshot(query(clientsCol), snapshot => { setClients(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); }, (error) => console.error("Client snapshot error:", error));
    const unsubServices = onSnapshot(query(servicesCol), snapshot => { setServices(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); }, (error) => console.error("Service snapshot error:", error));
    const unsubTransactions = onSnapshot(query(transactionsCol), snapshot => { setTransactions(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); }, (error) => console.error("Transaction snapshot error:", error));
    
    return () => { unsubAllOrders(); unsubClients(); unsubServices(); unsubTransactions(); };
  }, [currentUser]);

  // Funzione per costruire la query di base per gli ordini
  const buildOrdersQuery = (userId) => {
    const ordersCol = collection(db, "artifacts", appId, "users", userId, "orders");
    let q = query(ordersCol);

    if (selectedDate.month !== null) {
      const startDate = new Date(selectedDate.year, selectedDate.month, 1);
      const endDate = new Date(selectedDate.year, selectedDate.month + 1, 1);
      q = query(q, where("createdAt", ">=", startDate.toISOString()), where("createdAt", "<", endDate.toISOString()));
    } else {
      const startDate = new Date(selectedDate.year, 0, 1);
      const endDate = new Date(selectedDate.year + 1, 0, 1);
      q = query(q, where("createdAt", ">=", startDate.toISOString()), where("createdAt", "<", endDate.toISOString()));
    }
    
    if (orderStatusFilter !== 'all' && orderStatusFilter !== 'active' && orderStatusFilter !== 'urgent') {
        q = query(q, where("status", "==", orderStatusFilter));
    } else if (orderStatusFilter === 'active') {
        q = query(q, where("status", "!=", "Ritirato"));
    } else if (orderStatusFilter === 'urgent') {
        q = query(q, where("priority", "==", true), where("status", "!=", "Ritirato"));
    }
    return query(q, orderBy("createdAt", "desc"));
  };


  // Effetto per CARICARE LA PRIMA PAGINA di ordini quando i filtri cambiano
  useEffect(() => {
    if (!currentUser) return;
    setIsLoadingOrders(true);

    const userId = currentUser.uid;
    const baseQuery = buildOrdersQuery(userId);
    const q = query(baseQuery, limit(ORDERS_PER_PAGE));
    
    const unsubscribe = onSnapshot(q, (documentSnapshots) => {
        const newOrders = documentSnapshots.docs.map(doc => ({...doc.data(), id: doc.id}));
        setOrders(newOrders);

        const lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
        setLastOrderSnapshot(lastVisible);
        
        setHasMoreOrders(newOrders.length === ORDERS_PER_PAGE);
        setIsLoadingOrders(false);
    }, (error) => {
        console.error("Errore nello snapshot degli ordini:", error);
        setIsLoadingOrders(false);
    });

    return () => unsubscribe();
    
  }, [currentUser, orderStatusFilter, selectedDate]);


  // Effetto per AGGIORNARE I CONTEGGI
  useEffect(() => {
    if (!allOrders) return;

    const dateFilteredOrders = allOrders.filter(order => {
        const orderDate = new Date(order.createdAt);
        if (selectedDate.month !== null) {
            return orderDate.getFullYear() === selectedDate.year && orderDate.getMonth() === selectedDate.month;
        }
        return orderDate.getFullYear() === selectedDate.year;
    });

    const counts = {
        'In Lavorazione': dateFilteredOrders.filter(o => o.status === 'In Lavorazione').length,
        'Pronto': dateFilteredOrders.filter(o => o.status === 'Pronto').length,
        'Ritirato': dateFilteredOrders.filter(o => o.status === 'Ritirato').length,
        'active': dateFilteredOrders.filter(o => o.status !== 'Ritirato').length,
        'urgent': dateFilteredOrders.filter(o => o.status !== 'Ritirato' && o.priority).length,
        'all': dateFilteredOrders.length
    };
    
    setFilterCounts(counts);

  }, [allOrders, selectedDate]); 


  const fetchMoreOrders = async () => {
    if (!currentUser || !lastOrderSnapshot) return;
    setIsLoadingOrders(true);
    const userId = currentUser.uid;
    
    const baseQuery = buildOrdersQuery(userId);
    const q = query(baseQuery, startAfter(lastOrderSnapshot), limit(ORDERS_PER_PAGE));
    
    const documentSnapshots = await getDocs(q);
    
    const newOrders = documentSnapshots.docs.map(doc => ({...doc.data(), id: doc.id}));
    setOrders(prevOrders => [...prevOrders, ...newOrders]);
    
    const lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
    setLastOrderSnapshot(lastVisible);
    
    setHasMoreOrders(newOrders.length === ORDERS_PER_PAGE);
    setIsLoadingOrders(false);
  };
  
  const navigateTo = (view, filter = 'all') => {
    setActiveView(view);
    if (view === 'orders') {
        setOrderStatusFilter(filter);
    }
    setSearchTerm('');
  };
  
  const addOrder = async (orderData, selectedClientId) => {
    if(!currentUser || !db) return;
    const userId = currentUser.uid;
    let clientToUse;
    
    const newOrderNumber = parseInt(String(Date.now()).slice(-6));

    if (selectedClientId) {
        clientToUse = clients.find(c => c.id === selectedClientId);
    } else if (orderData.clientName) {
        const newClient = { name: orderData.clientName, phone: orderData.clientPhone, email: orderData.clientEmail, joinDate: new Date().toISOString().split('T')[0] };
        const docRef = await addDoc(collection(db, "artifacts", appId, "users", userId, "clients"), newClient);
        clientToUse = { ...newClient, id: docRef.id };
    } else {
        console.warn("Per favore, seleziona o inserisci un nome cliente.");
        return;
    }
    if (!clientToUse) {
        console.error("Errore: Cliente non trovato o non creato.");
        return;
    }
    
    const finalOrderData = {
        clientId: clientToUse.id,
        clientName: clientToUse.name,
        orderNumber: newOrderNumber,
        garments: orderData.garments,
        status: 'In Lavorazione',
        createdAt: new Date().toISOString(),
        dueDate: orderData.dueDate,
        total: orderData.total,
        priority: orderData.priority || false,
    };
    
    const orderDocRef = await addDoc(collection(db, "artifacts", appId, "users", userId, "orders"), finalOrderData);
    await setDoc(doc(db, "artifacts", appId, "public_orders", orderDocRef.id), { status: 'In Lavorazione', orderNumber: newOrderNumber });
    setIsOrderModalOpen(false);
  };

  const deleteOrder = async () => { if(!currentUser || !db || !orderToDelete) return; await deleteDoc(doc(db, "artifacts", appId, "users", currentUser.uid, "orders", orderToDelete.id)); await deleteDoc(doc(db, "artifacts", appId, "public_orders", orderToDelete.id)); setIsConfirmModalOpen(false); setOrderToDelete(null); };
  
  const onDeleteClientRequest = (client) => {
    setClientToDelete(client);
    setIsConfirmDeleteClientModalOpen(true);
  };

  const handleDeleteClient = async () => {
    if (!currentUser || !db || !clientToDelete) return;
    await deleteDoc(doc(db, "artifacts", appId, "users", currentUser.uid, "clients", clientToDelete.id));
    setIsConfirmDeleteClientModalOpen(false);
    setClientToDelete(null);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
      if(!currentUser || !db) return;
      const userId = currentUser.uid;
      const orderRef = doc(db, "artifacts", appId, "users", userId, "orders", orderId);
      const publicOrderRef = doc(db, "artifacts", appId, "public_orders", orderId);
      const batch = writeBatch(db);
      
      batch.update(orderRef, { status: newStatus });
      batch.update(publicOrderRef, { status: newStatus });

      if (newStatus === 'Ritirato') {
        const orderDoc = await getDoc(orderRef);
        if(orderDoc.exists()){
            const orderData = orderDoc.data();
            const transactionRef = doc(collection(db, "artifacts", appId, "users", userId, "transactions"));
            batch.set(transactionRef, {
                orderId: orderId,
                ...orderData,
                status: 'Ritirato',
                retiredAt: new Date().toISOString()
            });
        }
      }
      
      await batch.commit();
  };
  const openEditClientModal = (client) => { setEditingClient(client); setIsEditClientModalOpen(true); };
  const handleUpdateClient = async () => { if (!currentUser || !db || !editingClient) return; const { id, ...clientData } = editingClient; await setDoc(doc(db, "artifacts", appId, "users", currentUser.uid, "clients", id), clientData); setIsEditClientModalOpen(false); setEditingClient(null); };
  const handleAddClient = async (clientData) => { if(!currentUser || !db) return; await addDoc(collection(db, "artifacts", appId, "users", currentUser.uid, "clients"), clientData); setIsNewClientModalOpen(false); };
  const handleAddService = async (serviceData) => { if(!currentUser || !db) return; await addDoc(collection(db, "artifacts", appId, "users", currentUser.uid, "services"), serviceData);};
  const handleUpdateService = async (serviceId, serviceData) => { if(!currentUser || !db) return; await updateDoc(doc(db, "artifacts", appId, "users", currentUser.uid, "services", serviceId), serviceData); };
  const handleDeleteService = async (serviceId) => { if(!currentUser || !db) return; await deleteDoc(doc(db, "artifacts", appId, "users", currentUser.uid, "services", serviceId));};
  const handleExportData = () => { const dataStr = JSON.stringify({ clients, orders: allOrders, services }, null, 2); const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr); const exportFileDefaultName = `backup-lavanderia-strasso-${new Date().toISOString().slice(0,10)}.json`; const linkElement = document.createElement('a'); linkElement.setAttribute('href', dataUri); linkElement.setAttribute('download', exportFileDefaultName); linkElement.click(); };
  const handleRestoreDataClick = () => { fileInputRef.current.click(); };
  const handleFileChange = (e) => { const file = e.target.files[0]; if(!file) return; const reader = new FileReader(); reader.onload = (event) => { try { const restoredData = JSON.parse(event.target.result); if(restoredData.clients && restoredData.orders) { setDataToRestore(restoredData); setIsRestoreConfirmModalOpen(true); } else { console.warn("File di backup non valido."); } } catch(error) { console.error("Errore nella lettura del file di backup:", error); } }; reader.readAsText(file); e.target.value = null; };
  const confirmRestoreData = async () => { if(!currentUser || !db || !dataToRestore) return; setIsRestoreConfirmModalOpen(false); const userId = currentUser.uid; const batch = writeBatch(db); try { const collections = ["orders", "clients", "services"]; for (const col of collections) { const snapshot = await getDocs(collection(db, "artifacts", appId, "users", userId, col)); snapshot.forEach(doc => batch.delete(doc.ref)); } dataToRestore.clients.forEach(c => { const {id, ...data} = c; batch.set(doc(collection(db, "artifacts", appId, "users", userId, "clients"), id), data); }); dataToRestore.orders.forEach(o => { const {id, ...data} = o; batch.set(doc(collection(db, "artifacts", appId, "users", userId, "orders"), id), data); }); if(dataToRestore.services) { dataToRestore.services.forEach(s => { const {id, ...data} = s; batch.set(doc(collection(db, "artifacts", appId, "users", userId, "services"), id), data); }); } await batch.commit(); console.log("Ripristino completato con successo!"); } catch (error) { console.error("Errore durante il ripristino:", error); } finally { setDataToRestore(null); } };
  
  const handleClearDataRequest = (clearType) => {
    setPasswordError('');
    setIsPasswordConfirmOpen({ open: true, type: clearType });
  };

  const confirmClearData = async (clearType) => {
    if (!currentUser || !db) return;
    const userId = currentUser.uid;
    
    let collectionsToClear = [];
    if (clearType === 'all') {
      collectionsToClear = ["orders", "clients", "services", "transactions"];
    } else {
      collectionsToClear = ["orders", "transactions"];
    }

    try {
      const batch = writeBatch(db);
      for (const col of collectionsToClear) {
        const colRef = collection(db, "artifacts", appId, "users", userId, col);
        const snapshot = await getDocs(colRef);
        snapshot.forEach(doc => batch.delete(doc.ref));
      }
      
      if (clearType === 'all' || clearType === 'orders_only') {
          const publicOrdersSnapshot = await getDocs(collection(db, "artifacts", appId, "public_orders"));
          publicOrdersSnapshot.forEach(doc => batch.delete(doc.ref));
      }

      await batch.commit();
      alert("Dati cancellati con successo.");
    } catch (error) {
      console.error("Errore durante la cancellazione dei dati:", error);
      alert("Si è verificato un errore durante la cancellazione dei dati.");
    }
  };

  const handleConfirmDeletionWithPassword = async (password) => {
    if (!password) {
        setPasswordError("La password è richiesta.");
        return;
    }

    const user = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, password);

    try {
        await reauthenticateWithCredential(user, credential);
        await confirmClearData(isPasswordConfirmOpen.type);
        setIsPasswordConfirmOpen({ open: false, type: '' });
    } catch (error) {
        console.error("Errore di ri-autenticazione:", error);
        if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
            setPasswordError("Password errata. Riprova.");
        } else {
            setPasswordError("Si è verificato un errore. Riprova.");
        }
    }
  };
  
  const handlePrintCustomerReceipt = (order) => {
    setOrderForCustomerReceipt(order);

    setTimeout(() => {
        const printContentEl = document.getElementById('customer-receipt-to-print');
        if (!printContentEl) return;

        const images = printContentEl.querySelectorAll('img');
        const promises = [];

        images.forEach(img => {
            if (!img.complete) {
                promises.push(new Promise(resolve => {
                    img.onload = resolve;
                    img.onerror = resolve;
                }));
            }
        });

        Promise.all(promises).then(() => {
            const printContent = printContentEl.innerHTML;
            const printWindow = window.open('', '_blank', 'height=800,width=800');
            printWindow.document.write(`
                <html>
                    <head>
                        <title>Stampa Ricevuta</title>
                        <link rel="preconnect" href="https://fonts.googleapis.com">
                        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                        <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;700&display=swap" rel="stylesheet">
                        <style>
                            body { font-family: 'IBM Plex Mono', monospace; width: 80mm; margin: 0; padding: 3mm; box-sizing: border-box; color: black; } 
                            #receipt-content { font-weight: 700; text-shadow: 0 0 0.1px black; } 
                            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; } 
                            .logo { height: 30mm; width: 30mm; object-fit: contain; margin-right: 5mm; } 
                            .store-details { text-align: right; font-size: 11.5pt; } 
                            .store-name { font-weight: 700; font-size: 17.5pt; } 
                            .order-details { display: flex; justify-content: space-between; font-size: 12.5pt; margin-bottom: 8px; } 
                            .order-number-section { text-align: right; } 
                            .order-label { font-weight: 700; } 
                            .order-number { font-size: 19.5pt; font-weight: 700; } 
                            .items-table { width: 100%; font-size: 13.5pt; border-collapse: collapse; margin-top: 5px; } 
                            .item-row { vertical-align: top; } 
                            .item-name { padding-top: 5px; padding-right: 8px; word-break: break-word; font-weight: 700;} 
                            .item-price { padding-top: 5px; text-align: right; font-weight: 700; } 
                            .note-row { } 
                            .item-note { font-size: 11.5pt; font-style: normal; padding-left: 12px; padding-bottom: 5px; color: #000000; } 
                            .item-note strong { font-weight: bold; }
                            .service-type-tag { font-style: italic; font-size: 10.5pt; }
                            .hr-dashed { border: 0; border-top: 1px dashed black; margin: 8px 0; } 
                            .hr-solid { border: 0; border-top: 2px solid black; margin: 8px 0; } 
                            .total-section { text-align: right; margin-top: 12px; } 
                            .total-label { font-size: 19.5pt; font-weight: 700; } 
                            .footer { text-align: center; font-size: 10.5pt; margin-top: 12px; }
                            .qr-code { display: block; margin: 8px auto 0; width: 35mm; height: 35mm; }
                        </style>
                    </head>
                    <body>
                        ${printContent}
                    </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
            printWindow.close();
            setOrderForCustomerReceipt(null);
        });
    }, 50);
  };

  const handlePrintHangerTicket = (order) => { setOrderForHangerTicket(order); setTimeout(() => { const printContent = document.getElementById('hanger-ticket-to-print').innerHTML; const printWindow = window.open('', '_blank', 'height=600,width=400'); printWindow.document.write('<html><head><title>Stampa Etichetta</title>'); printWindow.document.write('<style>body { font-family: "Arial", sans-serif; } #hanger-ticket-content { font-size: 14pt; font-weight: bold; } .item-note { font-style: normal; font-weight: bold; color: #000; display: block; margin-top: 4px; font-size: 12pt; } .item-note strong { font-weight: bold; } hr { border-style: dashed; } h2 { font-size: 1.5rem; font-weight: bold; } .urgent-indicator { font-weight: bold; font-size: 16pt; text-align: center; border: 2px solid black; padding: 2px; margin-top: 5px; } .service-type-indicator { font-weight: bold; font-size: 14pt; text-align: center; border: 1px dashed black; padding: 2px; margin-top: 5px; } </style>'); printWindow.document.write('</head><body>');
        printWindow.document.write(printContent);
        printWindow.document.write('</div></body></html>');
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close(); setOrderForHangerTicket(null); }, 100); };
  
    // Funzione per aprire il modale di modifica, raggruppando i capi per quantità
    const handleOpenEditOrderModal = (order) => {
        const garmentGroups = order.garments.reduce((acc, garment) => {
            const key = `${garment.name}-${garment.price}-${garment.serviceDescription}-${garment.notes || ''}`;

            if (!acc[key]) {
                let serviceType = 'wash_iron'; // Default
                const desc = garment.serviceDescription || '';
                if (desc === 'Lavaggio + Stiro') serviceType = 'wash_iron';
                else if (desc === 'Solo Lavaggio') serviceType = 'wash_only';
                else if (desc === 'Solo Stiro') serviceType = 'iron_only';
                else if (desc === 'Lavaggio a Secco') serviceType = 'dry_clean';
                else if (desc === 'Servizio Personalizzato' || desc === 'Personalizzato') {
                    serviceType = 'custom_service';
                }

                acc[key] = {
                    id: Date.now() + Math.random(),
                    name: garment.name,
                    price: garment.price,
                    notes: garment.notes,
                    quantity: 0,
                    isCustom: desc === 'Personalizzato' || desc === 'Servizio Personalizzato',
                    serviceType: serviceType
                };
            }
            acc[key].quantity++;
            return acc;
        }, {});

        const groupedGarments = Object.values(garmentGroups);

        const processedOrder = {
            ...order,
            garments: groupedGarments,
        };

        setEditingOrder(processedOrder);
        setIsOrderModalOpen(true);
    };

  const handleUpdateOrder = async (orderId, updatedData) => { if(!currentUser || !db) return; const orderRef = doc(db, "artifacts", appId, "users", currentUser.uid, "orders", orderId); await updateDoc(orderRef, updatedData); setIsOrderModalOpen(false); setEditingOrder(null); }
  const openClientDetailModal = (client) => {
    setSelectedClientForDetail(client);
    setIsClientDetailModalOpen(true);
  };

  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const newOrdersToday = allOrders.filter(o => new Date(o.createdAt).toISOString().split('T')[0] === today).length;
    const retiredToday = transactions.filter(t => t.retiredAt && new Date(t.retiredAt).toISOString().split('T')[0] === today);
    const garmentsToday = retiredToday.reduce((sum, t) => sum + (t.garments ? t.garments.length : 0), 0);
    const newClientsToday = clients.filter(c => new Date(c.joinDate).toISOString().split('T')[0] === today).length;

    const dailySummary = { newOrders: newOrdersToday, newClients: newClientsToday, garments: garmentsToday };
    
    const topClients = Object.entries(
        transactions
        .filter(order => new Date(order.createdAt).getMonth() === new Date().getMonth())
        .reduce((acc, order) => {
            const clientName = order.clientName;
            if (!acc[clientName]) { acc[clientName] = 0; }
            acc[clientName] += order.total;
            return acc;
        }, {})
    )
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

    const urgentOrders = allOrders.filter(o => o.priority && o.status !== 'Ritirato');

    const ordersToPickup = allOrders.filter(o => o.status === 'Pronto').length;

    const focusItems = [];
    const dueTodayOrders = allOrders.filter(o => o.dueDate === today && o.status !== 'Ritirato');
    dueTodayOrders.forEach(order => {
        focusItems.push({ icon: <AlertCircle className="text-red-500" />, text: `L'ordine di ${order.clientName} è in scadenza oggi!` });
    });
    const oldReadyOrders = allOrders.filter(o => o.status === 'Pronto' && (new Date() - new Date(o.createdAt)) / (1000 * 60 * 60 * 24) > 7);
    if(oldReadyOrders.length > 0) {
      focusItems.push({ icon: <Phone size={16} className="text-blue-500" />, text: `Ci sono ${oldReadyOrders.length} ordini pronti da più di una settimana. Contatta i clienti.` });
    }

    if (focusItems.length === 0) {
        focusItems.push({icon: <CheckCircle size={16} className="text-green-500"/>, text: "Tutto sotto controllo. Ottimo lavoro!"});
    }
      
    return {
        activeOrders: allOrders.filter(o => o.status !== 'Ritirato').length,
        inProgressOrders: allOrders.filter(o => o.status === 'In Lavorazione').length,
        clients: clients.length,
        topClients,
        urgentOrders,
        focusItems,
        dailySummary,
        ordersToPickup,
    };
  }, [allOrders, clients, transactions]);
  
  const renderView = () => {
    switch (activeView) {
      case 'dashboard': return <DashboardView stats={stats} navigateTo={navigateTo} />;
      case 'orders': return <OrdersView orders={orders} clients={clients} onStatusChange={updateOrderStatus} onDeleteRequest={(order) => { setOrderToDelete(order); setIsConfirmModalOpen(true); }} onPrintCustomerReceipt={handlePrintCustomerReceipt} onPrintHangerTicket={handlePrintHangerTicket} onSearchChange={setSearchTerm} searchTerm={searchTerm} onOpenEditOrderModal={handleOpenEditOrderModal} orderStatusFilter={orderStatusFilter} onFilterChange={setOrderStatusFilter} filterCounts={filterCounts} selectedDate={selectedDate} onDateChange={setSelectedDate} onLoadMore={fetchMoreOrders} hasMore={hasMoreOrders} isLoading={isLoadingOrders} />;
      case 'clients': return <ClientsView clients={clients} onEditClient={openEditClientModal} onOpenNewClientModal={() => setIsNewClientModalOpen(true)} onDeleteClient={onDeleteClientRequest} onOpenClientDetail={openClientDetailModal}/>;
      case 'services': return <ServicesView services={services} onAddService={handleAddService} onUpdateService={handleUpdateService} onDeleteService={handleDeleteService} />;
      case 'settings': return <SettingsView onExportData={handleExportData} onRestoreDataClick={handleRestoreDataClick} onLogout={onLogout} navigateTo={navigateTo} currentUser={currentUser} onClearDataRequest={handleClearDataRequest} />;
      default: return <div>Vista non trovata</div>;
    }
  };

  return (
    <>
      <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Playfair+Display:wght@700&display=swap');
          body { 
              font-family: 'Montserrat', sans-serif; 
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              text-rendering: optimizeLegibility;
          }
          .font-display { font-family: 'Playfair Display', serif; }
          .printable-area { display: none; }
      `}</style>
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".json" />
      <div className="flex h-screen bg-gray-50 font-sans app-container" style={{ backgroundColor: COLORS.background }}>
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header navigateTo={navigateTo} isOnline={isOnline}/>
            {!isOnline && (
                <div className="bg-red-600 text-white text-center p-2 font-semibold flex items-center justify-center gap-2">
                    <WifiOff size={16} />
                    Modalità Offline: Le modifiche saranno sincronizzate al ritorno della connessione.
                </div>
            )}
          <main ref={mainContentRef} className="flex-1 overflow-y-auto p-4 sm:p-6 pb-24">{renderView()}</main>
          <button onClick={() => {setEditingOrder(null); setIsOrderModalOpen(true);}} className="fixed bottom-20 right-5 bg-gradient-to-r from-yellow-500 to-yellow-400 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform z-30"><Plus size={28} /></button>
          <BottomNav activeView={activeView} navigateTo={navigateTo} isVisible={isBottomNavVisible} />
        </div>
      </div>
      
      <div className="printable-area" id="customer-receipt-to-print">{orderForCustomerReceipt && <CustomerReceipt order={orderForCustomerReceipt} />}</div>
      <div className="printable-area" id="hanger-ticket-to-print">{orderForHangerTicket && <HangerTicket order={orderForHangerTicket} />}</div>

      <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onConfirm={deleteOrder} title="Conferma Eliminazione" message={`Sei sicuro di voler eliminare l'ordine di ${orderToDelete?.clientName}? L'azione è irreversibile.`} />
      <ConfirmationModal isOpen={isConfirmDeleteClientModalOpen} onClose={() => setIsConfirmDeleteClientModalOpen(false)} onConfirm={handleDeleteClient} title="Conferma Eliminazione Cliente" message={`Sei sicuro di voler eliminare il cliente ${clientToDelete?.name}? L'azione è irreversibile.`} />
      <ConfirmationModal isOpen={isRestoreConfirmModalOpen} onClose={() => setIsRestoreConfirmModalOpen(false)} onConfirm={confirmRestoreData} title="Conferma Ripristino" message="ATTENZIONE: Stai per sovrascrivere TUTTI i dati attuali con quelli del file di backup. L'azione è irreversibile. Continuare?" />
      <PasswordConfirmationModal
        isOpen={isPasswordConfirmOpen.open}
        onClose={() => setIsPasswordConfirmOpen({ open: false, type: '' })}
        onConfirm={handleConfirmDeletionWithPassword}
        title="Conferma Azione Irreversibile"
        message={`Sei assolutamente sicuro di voler cancellare ${isPasswordConfirmOpen.type === 'all' ? 'TUTTI i dati' : 'gli ordini e i dati contabili'}?`}
        error={passwordError}
      />
        <NewOrderModal
            isOpen={isOrderModalOpen}
            onClose={() => {
                setIsOrderModalOpen(false);
                setEditingOrder(null);
            }}
            clients={clients}
            services={services}
            onAddOrder={addOrder}
            onUpdateOrder={handleUpdateOrder}
            editingOrder={editingOrder}
        />

      <NewClientModal isOpen={isNewClientModalOpen} onClose={() => setIsNewClientModalOpen(false)} onAddClient={handleAddClient}/>
      {editingClient && (<Modal isOpen={isEditClientModalOpen} onClose={() => setIsEditClientModalOpen(false)} title="Modifica Cliente">
            <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700">Nome Cliente</label><input type="text" value={editingClient.name || ''} onChange={(e) => setEditingClient({...editingClient, name: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Telefono</label><input type="tel" value={editingClient.phone || ''} onChange={(e) => setEditingClient({...editingClient, phone: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Email</label><input type="email" value={editingClient.email || ''} onChange={(e) => setEditingClient({...editingClient, email: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Data di Nascita (opzionale)</label><input type="date" value={editingClient.birthDate || ''} onChange={(e) => setEditingClient({...editingClient, birthDate: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Data Iscrizione</label><input type="date" value={editingClient.joinDate ? new Date(editingClient.joinDate).toISOString().split('T')[0] : ''} onChange={(e) => setEditingClient({...editingClient, joinDate: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500" /></div>
                <div className="pt-4 border-t flex justify-end"><button onClick={handleUpdateClient} className="px-6 py-2 rounded-md text-white font-semibold transition-colors" style={{ backgroundColor: COLORS.primary }}> Salva Modifiche </button></div>
            </div>
      </Modal>)}
      <ClientDetailModal
        isOpen={isClientDetailModalOpen}
        onClose={() => setIsClientDetailModalOpen(false)}
        client={selectedClientForDetail}
        clientHistory={allOrders.filter(o => o.clientId === selectedClientForDetail?.id)}
        services={services}
      />
    </>
  );
}

// Componente wrapper per Google Analytics
const GoogleAnalytics = ({ measurementId }) => {
  useEffect(() => {
    // Non inizializzare se l'ID non è valido
    if (!measurementId || measurementId === "G-XXXXXXXXXX") {
      console.warn("Google Analytics: measurementId non impostato.");
      return;
    }

    const scriptId = 'google-analytics-script';
    if (document.getElementById(scriptId)) return;
    
    const gtagScript = document.createElement('script');
    gtagScript.id = scriptId;
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(gtagScript);

    const dataLayerScript = document.createElement('script');
    dataLayerScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}');
    `;
    document.head.appendChild(dataLayerScript);

  }, [measurementId]);

  return null;
};

// Componente principale che gestisce la logica di autenticazione e routing
export default function AppWrapper() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [viewMode, setViewMode] = useState('auth');
  const [activeView, setActiveView] = useState('dashboard');

  useEffect(() => {
    document.title = "Gestionale Strasso";
    if (!auth) {
        setIsLoadingAuth(false);
        return;
    };
    const unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setIsLoadingAuth(false);
        if (user) {
            setViewMode('app');
        } else {
            setViewMode('auth');
        }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
        const hash = window.location.hash;
        if (hash === '#track') {
            setViewMode('track');
        } else if (!currentUser) {
            setViewMode('auth');
        }
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [currentUser]);

  // Tracciamento delle visualizzazioni di pagina per Google Analytics
  useEffect(() => {
      if (viewMode === 'app' && typeof window.gtag === 'function') {
          window.gtag('event', 'page_view', {
              page_title: activeView,
              page_path: `/${activeView}`,
              page_location: window.location.href
          });
      }
  }, [activeView, viewMode]);


  const handleLogin = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const handleRegister = (email, password) => createUserWithEmailAndPassword(auth, email, password);
  const handleLogout = () => signOut(auth);

  if (isLoadingAuth) {
      return <div className="w-screen h-screen flex justify-center items-center bg-gray-100"><p>Caricamento in corso...</p></div>;
  }
  
  return (
    <>
        <GoogleAnalytics measurementId={GA_MEASUREMENT_ID} />
        {viewMode === 'track' && <TrackingView onBackToLogin={() => {window.location.hash = ''; setViewMode('auth')}} />}
        {viewMode === 'auth' && !currentUser && <AuthScreen onLogin={handleLogin} onRegister={handleRegister} onSwitchToTracking={() => setViewMode('track')} />}
        {viewMode === 'app' && currentUser && <LaundryApp currentUser={currentUser} onLogout={handleLogout} activeView={activeView} setActiveView={setActiveView} />}
    </>
  );
}

